import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Event from "../../../../core/consts/analytics-events";
import extractPhoneCountryCode from "../../utils/extract-phone-country-code";
import Button from "../../../../components/button/button";

import SixDigitField from "./six-digit-field";
import styles from "./otp.module.scss";
import clsx from "clsx";
import NavBar from "../../../../components/nav-bar/nav-bar";
import Modal from "../../../../components/modal/modal";
import { useDI } from "../../../../core/di";
import MyNexarContext from "../../../../components/my-nexar-context/my-nexar-context";

type CodeStatus = "sending" | "sent" | "sms-error" | "validation-error";

interface propsType {
  onClose: () => void;
  phone: string;
  onSuccess(): Promise<void>;
}

export function Otp(props: propsType) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<CodeStatus>("sending");
  const [code, setCode] = useState("");
  const [validating, setValidating] = useState(false);
  const [clientId, setClientId] = useState<string>("");
  const [canShowResend, setCanShowResend] = useState(true);
  const [resendTimeoutId, setResendTimeoutId] = useState<NodeJS.Timeout | null>(
    null
  );
  const { analytics, nexarApi, logger } = useDI();
  const { validateOtpCodeAndLogin } = useContext(MyNexarContext);

  // Once the resend button is hidden, wait another 10 seconds and show it again
  useEffect(() => {
    if (!canShowResend) {
      const timeoutId = setTimeout(() => setCanShowResend(true), 10000);
      setResendTimeoutId(timeoutId);
      return () => clearTimeout(timeoutId);
    }
  }, [canShowResend]);

  // Clear timeout and enable resend button immediately when SMS error occurs
  useEffect(() => {
    if (status === "sms-error") {
      if (resendTimeoutId) {
        clearTimeout(resendTimeoutId);
        setResendTimeoutId(null);
      }
      setCanShowResend(true);
    }
  }, [status, resendTimeoutId]);

  useEffect(() => {
    analytics?.track(Event.ConfirmationCodeViewed);
  }, []);

  const handleCodeSend = useCallback(
    async (isResend?: boolean) => {
      const [countryCode, rest] = extractPhoneCountryCode(props.phone);
      try {
        analytics?.track(
          isResend
            ? Event.ConfirmationCodeResendClicked
            : Event.ConfirmationCodeConfirmClicked
        );
        const res = await nexarApi.sendOtpCode(countryCode, rest);
        setStatus("sent");
        setClientId(res.clientId);
      } catch (e) {
        logger?.Error(e);
        setStatus("sms-error");
      }
    },
    [props.phone]
  );

  useEffect(() => {
    handleCodeSend();
  }, [handleCodeSend]);

  useEffect(() => {
    if (status === "sms-error" || status === "validation-error") {
      analytics?.track(Event.ConfirmationCodeErrorViewed);
    }
  }, [status]);

  function handleChange(newCode: string) {
    setCode(newCode);
    if (newCode.length === 6) {
      validate(newCode);
    } else {
      if (status === "validation-error") {
        setStatus("sent");
      }
    }
  }

  async function validate(newCode?: string) {
    setStatus("sent");
    setValidating(true);
    const codeToUse = newCode || code;
    const [countryCode, rest] = extractPhoneCountryCode(props.phone);

    try {
      await validateOtpCodeAndLogin(countryCode, rest, codeToUse, clientId);
      await props.onSuccess();
      setValidating(false);
    } catch (e) {
      logger?.Error(e);
      setStatus("validation-error");
      setValidating(false);
    }
  }

  const statusSection = () => {
    switch (status) {
      case "sms-error":
        return (
          <p
            className={clsx(styles.Status, styles.Error)}
            data-testid={"sms-status-error"}
          >
            {t("smsSendError")}
          </p>
        );
      case "validation-error":
        return (
          <p
            className={clsx(styles.Status, styles.Error)}
            data-testid={"validation-status-error"}
          >
            {t("incorrectOrExpired")}
          </p>
        );
      default:
        return (
          <p className={clsx(styles.Status)} data-testid={"validation-status"}>
            {t("codeSentTo", { phoneNumber: props.phone })}
          </p>
        );
    }
  };

  const codeSection = (
    <div className={styles.FieldGroup}>
      <SixDigitField className={styles.Digit} onChange={handleChange} />
    </div>
  );

  const handleResend = () => {
    handleCodeSend(true);
    setCanShowResend(false);
  };

  const handleClose = () => {
    analytics?.track(Event.ConfirmationCodeBackClicked);
    props.onClose();
  };

  const resendButton = () => {
    return (
      <button
        className={styles.Resend}
        onClick={handleResend}
        data-testid={"resend-code-button"}
      >
        {t("resendCode")}
      </button>
    );
  };

  const footer = (
    <footer className={styles.Footer}>
      <Button
        data-testid={"confirm"}
        action={validate}
        loading={validating}
        disabled={
          validating || code?.length !== 6 || status === "validation-error"
        }
      >
        {t("confirm")}
      </Button>
    </footer>
  );

  const getModalTitle = () => {
    switch (status) {
      case "sms-error":
        return t("tryAgain");
      case "validation-error":
        return t("tryAgain");
      default:
        return t("enterSmsCode");
    }
  };

  return (
    <Modal
      data-testid={"otp-error"}
      navBar={<NavBar onClick={handleClose} mode={"back"} />}
      title={getModalTitle()}
    >
      {statusSection()}
      {codeSection}
      {canShowResend && resendButton()}
      {footer}
    </Modal>
  );
}

export default Otp;
